import { useModel } from "@stem-sims/nexus"
import { Col, Row } from "react-bootstrap"
import { useParams } from "react-router"
import { PulseLoader } from "react-spinners"
import { createSection, deleteSection, getTopic, updateSection, updateTopic } from "shared/routes/curriculum/books"
import EditingItemPane from "./EditingItemPane"
import EditingListPane from "./EditingListPane"
import React from "react"
import TopicStandards from "./TopicStandards"
import WarningBanner from "components/General/WarningBanner"

export default function EditingTopic() {

    const { topicId } = useParams()

    const { loading: topicLoading, response: topic, refreshModel } = useModel({
        model: getTopic,
        props: { topicId }
    })

    const [defaultWeightSum, setDefaultWeightSum] = React.useState<number>(null)


    const onSubmitCallback = React.useCallback(async (values) => {
        await updateTopic({
            topicId: topicId,
            name: values.name,
            description: values.description,
            image: values.image,
            visible: values.visible,
            teacherOnly: values["teacher only"],
        })
        refreshModel()
    }, [topicId, refreshModel])

    const createSectionCallback = React.useCallback(async () => {
        await createSection({
            topicId: topicId,
        })
        refreshModel()
    }, [refreshModel, topicId])

    const updateSectionCallback = React.useCallback(async (values) => {
        await updateSection({
            sectionId: values.id,
            order: values.order,
        })
        refreshModel()
    }, [refreshModel])

    const deleteSectionCallback = React.useCallback(async (values) => {
        await deleteSection({ sectionId: values.id })
        refreshModel()
    }, [refreshModel])

    React.useEffect(() => {
        if (!topic) return
        const weightSum = topic.sections.reduce((acc, section) => {
            acc += section.defaultWeight
            return acc
        }, 0)
        setDefaultWeightSum(weightSum)
    }, [topic])


    if (!topic && topicLoading) {
        return <>
            <PulseLoader />
        </>
    }

    return <>
        <WarningBanner show={!topic.teacherOnly && defaultWeightSum === 0} text="All sections in this topic have 0 section weights." />
        <h3>{topic.name}</h3>
        <Row>
            <Col>
                <EditingItemPane
                    data={{
                        ...topic,
                        "teacher only": topic.teacherOnly,
                    }}
                    fields={[
                        { fieldName: "name", inputType: "input" },
                        { fieldName: "description", inputType: "textarea" },
                        { fieldName: "image", inputType: "image" },
                        { fieldName: "visible", inputType: "checkbox" },
                        { fieldName: "teacher only", inputType: "checkbox" },
                    ]}
                    itemTypeName="topic"
                    onSubmit={onSubmitCallback}
                />
                <TopicStandards topicId={topicId} />
            </Col>

            <Col>
                <h4>Sections</h4>
                <div className="d-flex align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <i className="fa fa-weight me-2"></i>
                        {defaultWeightSum?.toFixed(2) ?? 0}
                    </div>
                </div>
                {!topic?.sections && topicLoading && <>
                    <PulseLoader />
                </>}

                {(topic?.sections || topicLoading) && <>
                    <EditingListPane
                        parent={topic}
                        itemType="section"
                        items={topic?.sections ?? []}
                        createItem={createSectionCallback}
                        updateItem={updateSectionCallback}
                        deleteItem={deleteSectionCallback}
                    />
                </>}
            </Col>
        </Row>
    </>
}

